import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'
import { useMapsStore } from '@/stores/maps'
import { useGeoStore } from '@/stores/geo'

type OrgId = number | string
// Org layers are GeoJSON-like features: { id, uuid, type, geometry, properties }.
export type OrgLayer = any

const layerToFeature = (layer: any): OrgLayer => {
  const feature: OrgLayer = {
    id: layer.id,
    uuid: layer.code,
    type: 'Feature',
    geometry: layer.layer?.geometry ? layer.layer.geometry : null,
    properties: {
      type: layer.type,
      data_type: layer.data_type ?? null,
      parent_id: layer.parent_id ?? null,
      sort_order: layer.sort_order ?? null,
      visible: false,
      code: layer.code,
      name: layer.name,
      organization_id: layer.org_id,
      layer: layer.layer,
      created_at: layer.created_at,
      updated_at: layer.updated_at
    }
  }
  if (layer.children?.length) {
    feature.properties.children = layer.children.map(layerToFeature)
  }
  return feature
}

const patchLayerOrder = (orgId: OrgId, layers: OrgLayer[]) =>
  API({
    method: 'PATCH',
    url: `${HOST}/org/${orgId}/layers/order`,
    data: { layers: layers.map((l, idx) => ({ code: l.uuid, sort_order: idx })) }
  })

// Layer type 2 = subscription layer, status 0 = done
const SUBSCRIPTION_LAYER_TYPE = 2
const STATUS_DONE = 0

// Client-only state that a raw API payload must not wipe when a poll replaces the row.
const CLIENT_LAYER_STATE = ['visible', 'features', 'featureCollection', 'hiddenCaptures']

function persistLayerVisibility(orgId: OrgId, layers: OrgLayer[]) {
  const visibility: Record<string, boolean> = {}
  layers.forEach(l => {
    if (l.properties?.visible) visibility[l.uuid] = true
  })
  localStorage.setItem(`layerVisibility_${orgId}`, JSON.stringify(visibility))
}

function getSavedLayerVisibility(orgId: OrgId): Record<string, boolean> | null {
  const saved = localStorage.getItem(`layerVisibility_${orgId}`)
  if (!saved) return null
  try {
    const parsed = JSON.parse(saved)
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null
  } catch (e) {
    console.warn('Failed to parse layer visibility from localStorage:', e)
    return null
  }
}

interface LayersState {
  dates: any[]
  selectedRange: any
  orgLayers: Record<string, OrgLayer[]>
  selectedFeature: { feature: any, coordinates: any, isVisible: boolean }
  // Track active result per layer for chart-map interaction
  activeResults: Record<string, any>
  // Batch-fetched subscription results by orgId and layerCode
  subscriptionResults: Record<string, Record<string, any>>
  // PlanetScope mosaic selection per layer (detail page writes, sidebar variant reads)
  planetscopeSelection: Record<string, any>
  // Mapbox view toggles (labels / 3D buildings / 3D terrain) per map, for URL sharing
  mapViewSettings: Record<string, any>
  // Order product bundle selection per layer (detail page writes, sidebar variant reads)
  orderProductBundle: Record<string, any>
  // PlanetVars quarter selection per layer (detail page writes, sidebar variant reads)
  planetVarsSelection: Record<string, any>
  // LULC active month per parent layer (detail chart hover writes, sidebar variant reads).
  // Keyed by parent layers.code; value is the active layer_lulc child code.
  lulcActiveMonths: Record<string, string>
  // Multi-select: keyed by orgId, value is { [layerCode]: true }
  selectedLayers: Record<string, Record<string, boolean>>
  // Copy/paste clipboard for a single layer
  clipboardLayer: { orgId: OrgId, layerCode: string, name: string } | null
}

export const useLayersStore = defineStore('layers', {
  state: (): LayersState => ({
    dates: [],
    selectedRange: null,
    orgLayers: {},
    selectedFeature: {
      feature: null,
      coordinates: null,
      isVisible: false
    },
    activeResults: {},
    subscriptionResults: {},
    planetscopeSelection: {},
    mapViewSettings: {},
    orderProductBundle: {},
    planetVarsSelection: {},
    lulcActiveMonths: {},
    selectedLayers: {},
    clipboardLayer: null
  }),

  getters: {
    getFilteredDates: (state): any[] => {
      const filters = useGeoStore().projectFilters
      const templateIds = filters?.templateIds
      if (!templateIds || templateIds.length === 0) return state.dates
      return state.dates.filter(d => {
        // dates have id like "template_42" or "radar", extract numeric part
        const match = String(d.id).match(/template_(\d+)/)
        if (!match) return true // keep non-template entries (e.g. radar)
        const id = parseInt(match[1])
        return templateIds.includes(id) || templateIds.includes(String(id))
      })
    },
    getOrgLayers: state => (orgId: OrgId): OrgLayer[] => {
      return state.orgLayers[orgId] || []
    },
    hasOrgLayers: state => (orgId: OrgId) => {
      return state.orgLayers[orgId] !== undefined
    },
    getOrgLayerById: state => (orgId: OrgId, layerId: string): OrgLayer | undefined => {
      const orgLayers = state.orgLayers[orgId] || []
      for (const layer of orgLayers) {
        if (layer.uuid === layerId) return layer
        const children = layer.properties?.children || []
        for (const child of children) {
          if (child.uuid === layerId) return child
        }
      }
      return undefined
    },
    getActiveResult: state => (layerId: string) => {
      return state.activeResults[layerId] || null
    },
    getSubscriptionResults: state => (orgId: OrgId, layerCode: string) => {
      return state.subscriptionResults?.[orgId]?.[layerCode] || null
    },
    getPlanetscopeSelection: state => (layerId: string) => {
      return state.planetscopeSelection[layerId] || null
    },
    getMapViewSettings: state => (mapId: string) => {
      return state.mapViewSettings[mapId] || null
    },
    getOrderProductBundle: state => (layerId: string) => {
      return state.orderProductBundle[layerId] || null
    },
    getPlanetVarsSelection: state => (layerId: string) => {
      return state.planetVarsSelection[layerId] || null
    },
    getLulcActiveMonth: state => (parentCode: string) => {
      return state.lulcActiveMonths[parentCode] || null
    },
    isLayerSelected: state => (orgId: OrgId, layerCode: string) => {
      return !!(state.selectedLayers[orgId]?.[layerCode])
    },
    getSelectedLayerCount: state => (orgId: OrgId) => {
      return Object.keys(state.selectedLayers[orgId] || {}).length
    },
  },

  actions: {
    // --- Timeline dates --------------------------------------------------------

    addDates(payload: any) {
      try {
        const existingDatesMap = new Map<string, any>()

        if (this.dates && Array.isArray(this.dates)) {
          this.dates.forEach(date => {
            if (date && date.id) {
              existingDatesMap.set(date.id, date)
            }
          })
        }

        const payloadArray = Array.isArray(payload) ? payload : [payload]
        payloadArray.forEach(newDate => {
          if (!newDate || typeof newDate !== 'object') return

          if (newDate.id === 'features' && newDate.layers && Array.isArray(newDate.layers)) {
            newDate.layers.forEach((templateLayer: any) => {
              if (templateLayer && templateLayer.id) {
                const safeTemplateLayer = {
                  id: templateLayer.id,
                  name: templateLayer.name || 'Unknown Template',
                  color: templateLayer.color || 'blue',
                  dates: Array.isArray(templateLayer.dates) ? [...templateLayer.dates] : []
                }
                existingDatesMap.set(templateLayer.id, safeTemplateLayer)
              }
            })
          }
          else if (newDate.id) {
            const safeDateObject = {
              id: newDate.id,
              name: newDate.name || newDate.id || 'Unknown',
              color: newDate.color || 'blue',
              dates: Array.isArray(newDate.dates) ? [...newDate.dates] : []
            }
            existingDatesMap.set(newDate.id, safeDateObject)
          }
        })

        this.dates = Array.from(existingDatesMap.values())
      } catch (err) {
        console.error('Error in addDates:', err)
      }
    },
    clearDates() {
      this.dates = []
    },
    removeDates(dateIds: string[]) {
      if (!Array.isArray(dateIds) || !this.dates) return
      const idsToRemove = new Set(dateIds)
      this.dates = this.dates.filter(date => !idsToRemove.has(date.id))
    },
    setSelectedRange(payload: any) {
      this.selectedRange = payload
    },

    // --- Org layers ------------------------------------------------------------

    setOrgLayers({ orgId, layers }: { orgId: OrgId, layers: OrgLayer[] }) {
      const existingLayers = this.orgLayers[orgId] || []
      const existingLayersMap = new Map(existingLayers.map(layer => [layer.uuid, layer]))

      const mergedLayers = layers.map(newLayer => {
        const existingLayer = existingLayersMap.get(newLayer.uuid)

        if (existingLayer && existingLayer.properties) {
          // Preserve existing client-side state when merging
          const preserved: Record<string, any> = {}
          if (existingLayer.properties.features) {
            preserved.features = existingLayer.properties.features
            preserved.featureCollection = existingLayer.properties.featureCollection || {
              type: 'FeatureCollection',
              features: existingLayer.properties.features
            }
          }
          if (existingLayer.properties.hiddenCaptures) {
            preserved.hiddenCaptures = existingLayer.properties.hiddenCaptures
          }
          if (existingLayer.properties.visible !== undefined) {
            preserved.visible = existingLayer.properties.visible
          }

          if (Object.keys(preserved).length > 0) {
            return {
              ...newLayer,
              properties: {
                ...newLayer.properties,
                ...preserved
              }
            }
          }
        }

        return newLayer
      })

      // Order comes from server (sort_order column). Restore visibility only.
      let orderedLayers = mergedLayers

      // Restore saved visibility from localStorage on fresh page load
      const visibilityMap = getSavedLayerVisibility(orgId)
      if (visibilityMap && existingLayers.length === 0) {
        orderedLayers = orderedLayers.map(l => {
          if (visibilityMap[l.uuid]) {
            return { ...l, properties: { ...l.properties, visible: true } }
          }
          return l
        })
      }

      this.orgLayers = {
        ...this.orgLayers,
        [orgId]: orderedLayers
      }
    },

    insertOrgLayer(orgId: OrgId, layer: OrgLayer) {
      if (!this.orgLayers[orgId]) {
        this.orgLayers[orgId] = []
      }
      this.orgLayers[orgId] = [layer, ...this.orgLayers[orgId]]
    },

    async fetchSubscriptionResults({ orgId, layerCodes }: { orgId: OrgId, layerCodes: string[] }) {
      if (!layerCodes.length) return {}

      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/org/${orgId}/layers/subscription/results/batch`,
          data: { layer_codes: layerCodes }
        })

        this.subscriptionResults = {
          ...this.subscriptionResults,
          [orgId]: response.data
        }
        return response.data
      } catch (error) {
        console.error('Error fetching subscription results:', error)
        return {}
      }
    },

    async fetchOrgLayers({ orgId }: { orgId: OrgId }) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/layers/all`
        })

        const layers: OrgLayer[] = response.data.map(layerToFeature)

        this.setOrgLayers({ orgId, layers })

        // Batch fetch subscription results for completed or tile-generating subscription layers.
        // Status 10 (generate_tiles) means the results JSON exists and tiles are being
        // generated progressively — earlier results may already have viewable tiles.
        const STATUS_GENERATING_TILES = 10
        const subscriptionLayers = layers.filter(
          l => l.properties.type === SUBSCRIPTION_LAYER_TYPE &&
            (l.properties.layer?.status === STATUS_DONE || l.properties.layer?.status === STATUS_GENERATING_TILES)
        )
        const layerCodes = subscriptionLayers.map(l => l.properties.code)

        if (layerCodes.length > 0) {
          this.fetchSubscriptionResults({ orgId, layerCodes })
        }

        return layers
      } catch (error) {
        console.error('Error fetching organization layers:', error)
      }
    },

    addOrgLayer({ orgId, layer }: { orgId: OrgId, layer: any }) {
      const featureLayer = layerToFeature(layer)
      this.insertOrgLayer(orgId, featureLayer)
      return featureLayer
    },

    updateOrgLayer({ orgId, layer }: { orgId: OrgId, layer: any }) {
      // The store keys layers by ``uuid`` (mapped from the API's ``code``) and
      // nests the API payload under ``properties.layer``. Callers sometimes pass
      // a raw response and sometimes a pre-converted feature — normalize here so
      // polling from variants like LayerLulcPreview actually updates the row.
      let featureLayer = layer
      if (!(layer && layer.uuid)) {
        featureLayer = layerToFeature(layer)
        const existing = this.getOrgLayerById(orgId, featureLayer.uuid)
        if (existing?.properties) {
          const preserved: Record<string, any> = {}
          for (const key of CLIENT_LAYER_STATE) {
            if (existing.properties[key] !== undefined) preserved[key] = existing.properties[key]
          }
          if (featureLayer.properties.children === undefined && existing.properties.children) {
            preserved.children = existing.properties.children
          }
          featureLayer = { ...featureLayer, properties: { ...featureLayer.properties, ...preserved } }
        }
      }
      if (this.orgLayers[orgId]) {
        const replace = (layers: OrgLayer[]): OrgLayer[] => layers.map(l => {
          if (l.uuid === featureLayer.uuid) return featureLayer
          const children = l.properties?.children
          if (!children?.length) return l
          const nextChildren = replace(children)
          if (nextChildren.every((c, i) => c === children[i])) return l
          return { ...l, properties: { ...l.properties, children: nextChildren } }
        })
        this.orgLayers[orgId] = replace(this.orgLayers[orgId])
        persistLayerVisibility(orgId, this.orgLayers[orgId])
      }
      return featureLayer
    },

    updateOrgLayerFeatures({ orgId, layerId, features }: { orgId: OrgId, layerId: string, features: any[] }) {
      if (this.orgLayers[orgId]) {
        // Extract thumbUrl from resources for each feature (for image markers)
        const processedFeatures = features.map(f => {
          const resources = f.properties?.resources
          const thumbUrl = resources?.find((r: any) => r.url?.includes('small'))?.url
                        || resources?.find((r: any) => r.url?.includes('thumbnail'))?.url
          if (thumbUrl) {
            return {
              ...f,
              properties: {
                ...f.properties,
                thumbUrl
              }
            }
          }
          return f
        })

        this.orgLayers[orgId] = this.orgLayers[orgId].map(l => {
          if (l.uuid === layerId) {
            return {
              ...l,
              properties: {
                ...l.properties,
                features: processedFeatures,
                featureCollection: {
                  type: 'FeatureCollection',
                  features: processedFeatures
                }
              }
            }
          }
          return l
        })
      }
      return features
    },

    async moveOrgLayerUp({ orgId, layerId }: { orgId: OrgId, layerId: string }) {
      if (this.orgLayers[orgId]) {
        const layers = [...this.orgLayers[orgId]]
        const index = layers.findIndex(layer => layer.uuid === layerId)
        if (index > 0) {
          const temp = layers[index - 1]
          layers[index - 1] = layers[index]
          layers[index] = temp
          this.orgLayers = { ...this.orgLayers, [orgId]: layers }
        }
      }
      try {
        await patchLayerOrder(orgId, this.orgLayers[orgId] || [])
      } catch (error) {
        console.error('Failed to persist layer order:', error)
      }
    },

    async moveOrgLayerDown({ orgId, layerId }: { orgId: OrgId, layerId: string }) {
      if (this.orgLayers[orgId]) {
        const layers = [...this.orgLayers[orgId]]
        const index = layers.findIndex(layer => layer.uuid === layerId)
        if (index >= 0 && index < layers.length - 1) {
          const temp = layers[index + 1]
          layers[index + 1] = layers[index]
          layers[index] = temp
          this.orgLayers = { ...this.orgLayers, [orgId]: layers }
        }
      }
      try {
        await patchLayerOrder(orgId, this.orgLayers[orgId] || [])
      } catch (error) {
        console.error('Failed to persist layer order:', error)
      }
    },

    async reorderOrgLayers({ orgId, layers }: { orgId: OrgId, layers: OrgLayer[] }) {
      this.orgLayers = { ...this.orgLayers, [orgId]: layers }
      try {
        await patchLayerOrder(orgId, layers)
      } catch (error) {
        console.error('Failed to persist layer order:', error)
      }
    },

    async reorderChildren({ orgId, parentUuid, children }: { orgId: OrgId, parentUuid: string, children: OrgLayer[] }) {
      if (this.orgLayers[orgId]) {
        this.orgLayers = {
          ...this.orgLayers,
          [orgId]: this.orgLayers[orgId].map(l =>
            l.uuid === parentUuid
              ? { ...l, properties: { ...l.properties, children } }
              : l
          )
        }
      }
      try {
        await patchLayerOrder(orgId, children)
      } catch (error) {
        console.error('Failed to persist children order:', error)
      }
    },

    async renameOrgLayer({ orgId, layerId, name }: { orgId: OrgId, layerId: string, name: string }) {
      try {
        const trimmedName = typeof name === 'string' ? name.trim() : ''
        if (!orgId || !layerId || !trimmedName) {
          throw new Error('Missing required parameters: orgId, layerId, and name are required')
        }

        await API({
          method: 'PATCH',
          url: `${HOST}/org/${orgId}/layers/${layerId}/name`,
          data: { name: trimmedName }
        })

        if (this.orgLayers[orgId]) {
          this.orgLayers[orgId] = this.orgLayers[orgId].map(l => {
            if (l.uuid === layerId) {
              return {
                ...l,
                properties: {
                  ...l.properties,
                  name: trimmedName
                }
              }
            }
            return l
          })
        }
        return true
      } catch (error) {
        console.error('Error renaming organization layer:', error)
        throw error
      }
    },

    async deleteOrgLayer({ orgId, layerId }: { orgId: OrgId, layerId: string }) {
      try {
        // Validate parameters
        if (!orgId || !layerId) {
          throw new Error('Missing required parameters: orgId and layerId are required')
        }

        // Log deletion attempt for debugging
        console.warn('DELETING LAYER:', {
          orgId,
          layerId,
          timestamp: new Date().toISOString(),
          stack: new Error().stack
        })

        await API({
          method: 'DELETE',
          url: `${HOST}/org/${orgId}/layers/${layerId}`
        })

        if (this.orgLayers[orgId]) {
          this.orgLayers[orgId] = this.orgLayers[orgId].filter(layer => layer.uuid !== layerId)
          persistLayerVisibility(orgId, this.orgLayers[orgId])
        }
        return true
      } catch (error) {
        console.error('Error deleting organization layer:', error)
        throw error
      }
    },

    setSelectedFeature({ feature, coordinates, isVisible }: { feature: any, coordinates: any, isVisible: boolean }) {
      this.selectedFeature = {
        feature,
        coordinates,
        isVisible
      }
    },

    updateLayerProperties({ orgId, layerId, properties }: { orgId?: OrgId | null, layerId: string, properties: Record<string, any> }) {
      const resolvedOrgId = orgId
      if (!resolvedOrgId || !layerId) {
        console.warn('updateLayerProperties: missing orgId or layerId', { orgId, layerId })
        return
      }

      if (this.orgLayers[resolvedOrgId]) {
        this.orgLayers[resolvedOrgId] = this.orgLayers[resolvedOrgId].map(l => {
          if (l.uuid === layerId) {
            return {
              ...l,
              properties: {
                ...l.properties,
                ...properties
              }
            }
          }
          return l
        })
        if ('visible' in properties) {
          persistLayerVisibility(resolvedOrgId, this.orgLayers[resolvedOrgId])
        }
      }
    },

    setActiveResult({ layerId, result }: { layerId: string, result: any }) {
      this.activeResults = {
        ...this.activeResults,
        [layerId]: result
      }
    },

    setPlanetscopeSelection({ layerId, selection }: { layerId: string, selection: any }) {
      this.planetscopeSelection = {
        ...this.planetscopeSelection,
        [layerId]: selection
      }
    },

    setMapViewSettings({ mapId, settings }: { mapId: string, settings: any }) {
      this.mapViewSettings = {
        ...this.mapViewSettings,
        [mapId]: settings
      }
    },

    setOrderProductBundle({ layerId, bundle }: { layerId: string, bundle: any }) {
      this.orderProductBundle = {
        ...this.orderProductBundle,
        [layerId]: bundle
      }
    },

    setPlanetVarsSelection({ layerId, selection }: { layerId: string, selection: any }) {
      this.planetVarsSelection = {
        ...this.planetVarsSelection,
        [layerId]: selection
      }
    },

    setLulcActiveMonth({ parentCode, monthCode }: { parentCode: string, monthCode: string | null }) {
      this.lulcActiveMonths = {
        ...this.lulcActiveMonths,
        [parentCode]: monthCode as string
      }
    },

    async createProjectDataLayer({ orgId, projectId, projectName }: { orgId: OrgId, projectId: number | string, projectName: string }) {
      const trimmedName = typeof projectName === 'string' ? projectName.trim() : ''
      if (!orgId || !projectId || !trimmedName) {
        throw new Error('Missing required parameters: orgId, projectId, and projectName are required')
      }

      // Check for existing layer with same project filter
      const existingLayers = this.getOrgLayers(orgId)
      const duplicate = existingLayers.find(layer => {
        if (layer.properties?.type !== 4) return false
        const filters = layer.properties?.layer?.data?.filter?.config
        if (!Array.isArray(filters) || filters.length !== 1) return false
        const f = filters[0]
        return f.type === 'ArrayFilter' && f.field_name === 'project_id' &&
          Array.isArray(f.config) && f.config.length === 1 && String(f.config[0]) === String(projectId)
      })

      if (duplicate) {
        toast({ title: 'Layer already exists', description: `A Project Data layer for "${trimmedName}" already exists.` })
        return duplicate
      }

      const response = await API({
        method: 'POST',
        url: `${HOST}/org/${orgId}/layers`,
        data: {
          org_id: orgId,
          type: 4,
          name: trimmedName,
          layer: {
            aoi: null,
            data: {
              filter: {
                type: 'AndFilter',
                config: [{
                  type: 'ArrayFilter',
                  field_name: 'project_id',
                  config: [String(projectId)]
                }]
              }
            }
          }
        }
      })

      const featureLayer = layerToFeature(response.data)
      this.insertOrgLayer(orgId, featureLayer)
      toast({ title: 'Layer created', description: `"${trimmedName}" added to Layers.` })
      return featureLayer
    },

    // --- Multi-select ----------------------------------------------------------

    toggleLayerSelection({ orgId, layerCode }: { orgId: OrgId, layerCode: string }) {
      const current = this.selectedLayers[orgId] || {}
      if (current[layerCode]) {
        const { [layerCode]: _, ...rest } = current
        this.selectedLayers = { ...this.selectedLayers, [orgId]: rest }
      } else {
        this.selectedLayers = { ...this.selectedLayers, [orgId]: { ...current, [layerCode]: true } }
      }
    },

    clearLayerSelection({ orgId }: { orgId: OrgId }) {
      this.selectedLayers = { ...this.selectedLayers, [orgId]: {} }
    },

    async groupSelectedLayers({ orgId }: { orgId: OrgId }) {
      const selected = this.selectedLayers[orgId] || {}
      const layerCodes = Object.keys(selected)
      if (layerCodes.length < 2) return

      try {
        await API({
          method: 'POST',
          url: `${HOST}/org/${orgId}/layers/group`,
          data: { layer_codes: layerCodes }
        })
        this.clearLayerSelection({ orgId })
        await this.fetchOrgLayers({ orgId })
        toast({ title: 'Group created', description: `${layerCodes.length} layers grouped.` })
      } catch (error) {
        console.error('Error grouping layers:', error)
        toast({ title: 'Failed to group layers', variant: 'destructive' })
        throw error
      }
    },

    // --- Clipboard ---------------------------------------------------------------

    copyLayer({ orgId, layerCode, name }: { orgId: OrgId, layerCode: string, name: string }) {
      this.clipboardLayer = { orgId, layerCode, name }
      toast({ title: 'Layer copied', description: 'Paste it onto a map from the layer menu.' })
    },

    // Duplicate a copied layer as a new, independent layer attached to the target map.
    async duplicateLayerToMap({ orgId, layerCode, mapUuid }: { orgId: OrgId, layerCode: string, mapUuid: string }) {
      try {
        await API({
          method: 'POST',
          url: `${HOST}/org/${orgId}/layers/${layerCode}/duplicate`,
          data: { map_uuid: mapUuid }
        })
        await this.fetchOrgLayers({ orgId })
        await useMapsStore().fetchMaps({ orgId })
        toast({ title: 'Layer pasted' })
      } catch (error) {
        console.error('Error duplicating layer:', error)
        toast({ title: 'Failed to paste layer', variant: 'destructive' })
        throw error
      }
    },

    // --- Groups ----------------------------------------------------------------

    async moveLayerToGroup({ orgId, layerCode, parentId }: { orgId: OrgId, layerCode: string, parentId: number | null }) {
      try {
        await API({
          method: 'PATCH',
          url: `${HOST}/org/${orgId}/layers/${layerCode}/parent`,
          data: { parent_id: parentId }
        })
        await this.fetchOrgLayers({ orgId })
      } catch (error) {
        console.error('Error moving layer to group:', error)
        toast({ title: 'Failed to move layer to group', variant: 'destructive' })
        throw error
      }
    },

    async removeLayerFromGroup({ orgId, layerCode }: { orgId: OrgId, layerCode: string }) {
      try {
        await API({
          method: 'PATCH',
          url: `${HOST}/org/${orgId}/layers/${layerCode}/parent`,
          data: { parent_id: null }
        })
        await this.fetchOrgLayers({ orgId })
      } catch (error) {
        console.error('Error removing layer from group:', error)
        toast({ title: 'Failed to remove layer from group', variant: 'destructive' })
        throw error
      }
    },

    async removeSelectedLayersFromGroup({ orgId }: { orgId: OrgId }) {
      const selected = this.selectedLayers[orgId] || {}
      const layerCodes = Object.keys(selected)
      if (layerCodes.length === 0) return

      try {
        await Promise.all(
          layerCodes.map(layerCode =>
            API({
              method: 'PATCH',
              url: `${HOST}/org/${orgId}/layers/${layerCode}/parent`,
              data: { parent_id: null }
            })
          )
        )
        this.clearLayerSelection({ orgId })
        await this.fetchOrgLayers({ orgId })
        toast({ title: 'Removed from group', description: `${layerCodes.length} layer${layerCodes.length > 1 ? 's' : ''} removed from group.` })
      } catch (error) {
        console.error('Error removing layers from group:', error)
        toast({ title: 'Failed to remove layers from group', variant: 'destructive' })
        throw error
      }
    },
  },
})
